import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, X, Camera } from 'lucide-react';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AuthenticatedPhoto } from './AuthenticatedPhoto';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const DEFAULT_MAX_PHOTOS = 20;

export interface SavedPhoto {
  id: string;
  file_name?: string;
  mime_type?: string;
  file_size?: number;
  created_at?: string;
}

interface PendingPhoto {
  key: string;
  file: File;
  url: string;
  error?: string | null;
}

interface PhotographSectionProps {
  /** Actual Labour record id. Present only in edit mode (the record exists). */
  entityId?: string;
  /** Photographs already persisted for the record (restored on edit). */
  savedPhotos?: SavedPhoto[];
  /** Whether the caller may add/remove photographs. */
  canModify?: boolean;
  maxPhotos?: number;
  /** Called whenever the set of valid pending (not yet uploaded) files changes. */
  onPendingChange?: (files: File[]) => void;
}

function clientValidate(file: File): string | null {
  if (!ACCEPTED.includes(file.type)) return 'Only JPEG, PNG and WebP images are allowed.';
  if (file.size === 0) return 'Empty file.';
  if (file.size > MAX_FILE_SIZE) return 'Each photograph must be 10 MB or smaller.';
  return null;
}

export function PhotographSection({
  entityId,
  savedPhotos = [],
  canModify = true,
  maxPhotos = DEFAULT_MAX_PHOTOS,
  onPendingChange,
}: PhotographSectionProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);

  const validPending = pending.filter((p) => !p.error);

  const syncPending = (next: PendingPhoto[]) => {
    setPending(next);
    onPendingChange?.(next.filter((p) => !p.error).map((p) => p.file));
  };

  const addFiles = (files: File[]) => {
    const remaining = maxPhotos - (savedPhotos?.length ?? 0) - validPending.length;
    if (remaining <= 0) {
      addToast(`This record can hold up to ${maxPhotos} photographs.`, 'error');
      return;
    }
    const selected = files.slice(0, remaining);
    if (files.length > remaining) {
      addToast(`Only ${remaining} more photograph${remaining === 1 ? '' : 's'} can be added.`, 'error');
    }
    const additions: PendingPhoto[] = selected.map((file) => {
      const error = clientValidate(file);
      return {
        key: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        url: error ? '' : URL.createObjectURL(file),
        error,
      };
    });
    const hasInvalid = additions.some((p) => p.error);
    if (hasInvalid) addToast('Some files were rejected. Review the highlighted files.', 'error');
    syncPending([...pending, ...additions]);
  };

  const removePending = (key: string) => {
    const target = pending.find((p) => p.key === key);
    if (target?.url) URL.revokeObjectURL(target.url);
    syncPending(pending.filter((p) => p.key !== key));
  };

  const clearPending = () => {
    pending.forEach((p) => { if (p.url) URL.revokeObjectURL(p.url); });
    syncPending([]);
  };

  const deleteSavedMutation = useMutation({
    mutationFn: (photoId: string) => api.delete(`/actual-status/${entityId}/photographs/${photoId}`),
    onMutate: (photoId) => setDeleteLoadingId(photoId),
    onSuccess: () => {
      addToast('Photograph removed.', 'success');
      if (entityId) {
        queryClient.invalidateQueries({ queryKey: ['actual-status', entityId] });
        queryClient.invalidateQueries({ queryKey: ['actual-status-photographs', entityId] });
      }
    },
    onError: (err: any) => addToast(err?.message || 'Unable to remove photograph.', 'error'),
    onSettled: () => setDeleteLoadingId(null),
  });

  const totalCount = (savedPhotos?.length ?? 0) + validPending.length;
  const atCapacity = totalCount >= maxPhotos;
  const canAdd = canModify && !atCapacity;

  return (
    <div className="space-y-1">
      {savedPhotos.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium mb-2">Saved photographs ({savedPhotos.length})</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {savedPhotos.map((photo) => (
              <div key={photo.id} className="relative border rounded-lg overflow-hidden group">
                <AuthenticatedPhoto
                  src={`/actual-status/${entityId}/photographs/${photo.id}`}
                  alt={photo.file_name || 'Photograph'}
                  className="w-full h-28 object-cover"
                  placeholderClassName="h-28"
                />
                {canModify && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-1 right-1 h-7 w-7 p-0 bg-black/40 hover:bg-black/60 text-white rounded-full"
                    loading={deleteLoadingId === photo.id}
                    onClick={() => deleteSavedMutation.mutate(photo.id)}
                    aria-label={`Remove photograph ${photo.file_name || photo.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium mb-2">New photographs ({validPending.length})</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {pending.map((p) => (
              <div key={p.key} className={cn('relative border rounded-lg overflow-hidden', p.error ? 'border-error' : 'border-default')}>
                {p.url ? (
                  <img src={p.url} alt={p.file.name} className="w-full h-28 object-cover" />
                ) : (
                  <div className="w-full h-28 flex items-center justify-center bg-muted text-xs px-2 text-center">{p.file.name}</div>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-1 right-1 h-7 w-7 p-0 bg-black/40 hover:bg-black/60 text-white rounded-full"
                  onClick={() => removePending(p.key)}
                  aria-label={`Remove ${p.file.name}`}
                >
                  <X size={14} />
                </Button>
                {p.error && <p className="absolute bottom-0 inset-x-0 bg-error text-white text-[11px] px-2 py-1 leading-tight">{p.error}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <label className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium cursor-pointer transition-colors',
          canAdd
            ? 'border-border hover:bg-accent text-primary'
            : 'border-border bg-muted text-muted-foreground cursor-not-allowed pointer-events-none',
        )} aria-label="Take Photo">
          <Camera size={16} /> Take Photo
          <input
            type="file"
            accept={ACCEPTED.join(',')}
            capture="environment"
            className="sr-only"
            data-testid="photo-file-input"
            disabled={!canAdd}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) addFiles(files);
              e.target.value = '';
            }}
          />
        </label>
        {validPending.length > 0 && (
          <Button type="button" variant="ghost" size="sm" onClick={clearPending}>Clear new photos</Button>
        )}
      </div>
    </div>
  );
}
